import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSyncAlt,
  faUser, faCalendarAlt, faDollarSign, faFilter,
  faImage, faTimes, faEnvelope, faPhone, faIdBadge, faArrowRight,
  faAddressCard, faMapPin, faBuilding, faUsers, faBriefcase,
  faGraduationCap, faFileContract, faBank, faIdCard,
  faStethoscope, faChild, faUserTie, faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { EmployesContext } from '../layout/CunstomEmployes';

// --- Configuration Cache Dexie ---
const db = new Dexie('EmployesCacheDB');
db.version(1).stores({ employes: 'key, data, timestamp' });
const CACHE_DURATION = 1000 * 60 * 60; // 1 heure

const API_URL = `${API_CONFIG2.apiUrl}employe/api_employe.php`;

const ManageEmployes = () => {
  const context = useContext(EmployesContext) || {};
  const { refreshKey, setSource } = context;

  // --- États Principaux ---
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 12 });
  const [dataSource, setDataSource] = useState('chargement');

  // --- États Modales & Formulaire ---
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  // --- Refs pour le contrôle ---
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isMounted = useRef(true);
  const fileInputRef = useRef(null);

  // --- Helpers ---
  const getPhotoUrl = (photoPath) => {
    if (!photoPath || photoPath === 'default.png') return null;
    return photoPath.startsWith('http') ? photoPath : `${API_CONFIG2.apiUrl}${photoPath.replace(/^\//, '')}`;
  };

  const showNotify = (msg, variant = 'success') => {
    setToast({ show: true, message: msg, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // --- FETCH LOGIC ---
  const fetchEmployees = useCallback(async (page = 1, search = '', forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `emp_p${page}_s${search}`;
    if (forceRefresh) setRefreshing(true); else setLoading(true);

    try {
      if (!forceRefresh) {
        const cached = await db.employes.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
          if (isMounted.current) {
            setEmployees(cached.data.data);
            setPagination(cached.data.pagination);
            setDataSource('cache');
            setLoading(false);
            return;
          }
        }
      }

      const response = await fetch(`${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`, {
        signal: controller.signal
      });
      const data = await response.json();

      if (data.status === 'success' && isMounted.current) {
        setEmployees(data.data);
        setPagination(data.pagination);
        setDataSource(forceRefresh ? 'live (actualisé)' : 'live');
        await db.employes.put({ key: cacheKey, data, timestamp: Date.now() });
      }
    } catch (err) {
      if (err.name !== 'AbortError' && isMounted.current) {
        showNotify("Erreur lors de la récupération", "danger");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [pagination.limit]);

  // --- Initialisation ---
  useEffect(() => {
    isMounted.current = true;
    fetchEmployees(1, '');
    return () => { isMounted.current = false; };
  }, [fetchEmployees]);

  // --- Search Debounce ---
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchEmployees(1, val);
    }, 600);
  };

  // --- Gestion du formulaire ---
  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      matricule: '',
      nom: '',
      postnom: '',
      prenom: '',
      date_naissance: '',
      lieu_naissance: '',
      nationalite: 'Congolaise',
      sexe: '',
      etat_civil: 'celibataire',
      nombre_enfants: 0,
      telephone: '',
      telephone2: '',
      email: '',
      province: '',
      ville: '',
      commune: '',
      quartier: '',
      adresse: '',
      photo: 'default.png',
      personne_urgence: '',
      telephone_urgence: '',
      lien_parente: '',
      date_embauche: '',
      date_depart: '',
      service: '',
      departement: '',
      fonction: '',
      categorie_professionnelle: '',
      niveau_etude: '',
      type_contrat: 'CDI',
      date_debut_contrat: '',
      date_fin_contrat: '',
      salaire_base: 0,
      devise: 'USD',
      banque: '',
      compte_bancaire: '',
      numero_cnss: '',
      numero_inpp: '',
      numero_nif: '',
      piece_identite: '',
      numero_piece: '',
      est_actif: 1,
      observation: ''
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormErrors({});
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (emp) => {
    setEditingEmployee(emp);
    setFormData({
      ...emp,
      date_naissance: emp.date_naissance || '',
      date_embauche: emp.date_embauche || '',
      date_depart: emp.date_depart || '',
      date_debut_contrat: emp.date_debut_contrat || '',
      date_fin_contrat: emp.date_fin_contrat || '',
      salaire_base: parseFloat(emp.salaire_base) || 0,
      nombre_enfants: parseInt(emp.nombre_enfants) || 0,
      est_actif: emp.est_actif !== undefined ? emp.est_actif : 1
    });
    setPhotoPreview(getPhotoUrl(emp.photo));
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData(prev => ({ ...prev, [name]: val }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nom?.trim()) errors.nom = 'Le nom est obligatoire';
    if (!formData.prenom?.trim()) errors.prenom = 'Le prénom est obligatoire';
    if (!formData.date_embauche) errors.date_embauche = "La date d'embauche est obligatoire";
    if (formData.salaire_base < 0) errors.salaire_base = 'Le salaire ne peut pas être négatif';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // --- CRUD Handlers ---
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const action = editingEmployee ? 'update' : 'create';
    const body = new FormData();

    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== undefined) {
        body.append(key, formData[key]);
      }
    });

    if (photoFile) body.append('photo', photoFile);
    if (editingEmployee) {
      body.append('_method', 'PUT');
      body.append('id', editingEmployee.id);
    }

    try {
      const res = await fetch(`${API_URL}?action=${action}`, {
        method: 'POST',
        body: body
      });
      const data = await res.json();
      if (data.status === 'success') {
        showNotify(data.message);
        setShowModal(false);
        resetForm();
        fetchEmployees(pagination.currentPage, searchTerm, true);
      } else {
        showNotify(data.message || 'Erreur inconnue', 'danger');
      }
    } catch (err) {
      showNotify("Erreur de connexion au serveur", "danger");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        showNotify('Employé supprimé');
        setShowDeleteModal(false);
        fetchEmployees(pagination.currentPage, searchTerm, true);
      } else {
        showNotify(data.message, 'danger');
      }
    } catch (err) {
      showNotify("Erreur de suppression", "danger");
    }
  };

  // --- Filtres Client-Side ---
  const filteredList = useMemo(() => {
    return employees.filter(emp => {
      const matchService = !filterService || emp.service === filterService;
      const matchStatut = !filterStatut || (emp.est_actif === 1 ? 'Actif' : 'Inactif') === filterStatut;
      return matchService && matchStatut;
    });
  }, [employees, filterService, filterStatut]);

  const uniqueServices = useMemo(() => [...new Set(employees.map(e => e.service).filter(Boolean))], [employees]);

  // --- Rendu ---
  return (
    <div className="monetra-main-wrapper">
      <style>{`
        /* ================================================================
           DESIGN GLASSMORPHISME PREMIUM – DARK & LUMINEUX
           ================================================================ */

        /* ---- Animate background ---- */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        :root {
          --glass-bg: rgba(15, 23, 42, 0.45);
          --glass-border: rgba(255, 255, 255, 0.08);
          --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
          --accent-blue: #3b82f6;
          --accent-cyan: #06b6d4;
          --accent-purple: #8b5cf6;
          --text-main: #f8fafc;
          --text-dim: #94a3b8;
          --glow-color: rgba(59, 130, 246, 0.15);
        }

        .monetra-main-wrapper {
          min-height: 100vh;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, sans-serif;
          color: var(--text-main);
          background: #020617;
          position: relative;
          overflow: hidden;
        }

        /* ---- Fond dynamique avec orbes lumineux ---- */
        .monetra-main-wrapper::before {
          content: '';
          position: fixed;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 30% 20%, rgba(59,130,246,0.08) 0%, transparent 50%),
                      radial-gradient(circle at 70% 80%, rgba(139,92,246,0.06) 0%, transparent 50%),
                      radial-gradient(circle at 10% 80%, rgba(6,182,212,0.05) 0%, transparent 40%);
          animation: gradientShift 20s ease infinite;
          z-index: 0;
          pointer-events: none;
        }

        .monetra-main-wrapper > * {
          position: relative;
          z-index: 1;
        }

        /* ---- Cartes glass ultra-transparentes ---- */
        .glass-card {
          background: var(--glass-bg);
          backdrop-filter: blur(20px) saturate(1.2);
          -webkit-backdrop-filter: blur(20px) saturate(1.2);
          border: 1px solid var(--glass-border);
          border-radius: 24px;
          box-shadow: var(--glass-shadow);
          transition: all 0.4s cubic-bezier(0.2, 0, 0, 1);
        }

        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 8px 40px rgba(59, 130, 246, 0.08), 0 0 80px rgba(59, 130, 246, 0.02);
        }

        /* ---- Carte employé ---- */
        .emp-grid-card {
          margin-top: 40px;
          position: relative;
          padding: 1.8rem 1.2rem 1.2rem;
          text-align: center;
          height: 100%;
          background: rgba(15, 23, 42, 0.35);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 24px;
          transition: all 0.4s cubic-bezier(0.2, 0, 0, 1);
          animation: fadeInUp 0.5s ease forwards;
          opacity: 0;
          animation-delay: calc(0.05s * var(--i, 0));
        }

        .emp-grid-card:hover {
          transform: translateY(-6px) scale(1.01);
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(59, 130, 246, 0.05);
        }

        .emp-grid-card .emp-photo-container {
          width: 96px;
          height: 96px;
          border-radius: 28px;
          margin: -64px auto 14px;
          border: 4px solid rgba(15, 23, 42, 0.9);
          box-shadow: 0 12px 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(59,130,246,0.15);
          overflow: hidden;
          background: #1e293b;
          transition: all 0.4s ease;
        }

        .emp-grid-card:hover .emp-photo-container {
          transform: scale(1.05) rotate(-2deg);
          border-color: var(--accent-blue);
          box-shadow: 0 16px 40px rgba(59,130,246,0.2);
        }

        .emp-photo-container img {
          width: 100%; height: 100%; object-fit: cover;
        }

        .emp-name {
          font-size: 1.15rem;
          font-weight: 700;
          margin-bottom: 0.1rem;
          background: linear-gradient(135deg, #fff 60%, #94a3b8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .emp-role {
          font-size: 0.75rem;
          color: var(--accent-cyan);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 1rem;
          display: block;
          font-weight: 500;
        }

        .emp-info-box {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(4px);
          border-radius: 14px;
          padding: 12px 10px;
          margin-bottom: 1.2rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 12px;
          text-align: left;
          border: 1px solid rgba(255,255,255,0.03);
        }

        .info-label {
          font-size: 0.6rem;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: block;
        }

        .info-value {
          font-size: 0.8rem;
          font-weight: 600;
          color: #e2e8f0;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .info-value.text-success {
          color: #4ade80;
        }

        /* ---- Badge de statut lumineux ---- */
        .status-pill {
          position: absolute;
          top: 14px;
          right: 14px;
          padding: 4px 12px;
          border-radius: 30px;
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.05);
        }

        .status-pill.active {
          background: rgba(34, 197, 94, 0.15);
          color: #4ade80;
          box-shadow: 0 0 20px rgba(34, 197, 94, 0.05);
        }

        .status-pill.inactive {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          box-shadow: 0 0 20px rgba(239, 68, 68, 0.05);
        }

        /* ---- Actions ---- */
        .card-actions-row {
          display: flex;
          justify-content: center;
          gap: 10px;
        }

        .action-btn {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.04);
          color: var(--text-dim);
          transition: all 0.25s ease;
          backdrop-filter: blur(4px);
        }

        .action-btn:hover {
          background: var(--accent-blue);
          color: white;
          border-color: var(--accent-blue);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59,130,246,0.2);
        }

        .action-btn.delete:hover {
          background: #ef4444;
          border-color: #ef4444;
          box-shadow: 0 6px 20px rgba(239,68,68,0.2);
        }

        /* ---- Header et recherche ---- */
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2.5rem;
          flex-wrap: wrap;
          gap: 1.5rem;
        }

        .page-header h2 {
          font-weight: 700;
          letter-spacing: -0.5px;
        }

        .page-header h2 span {
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-cyan));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .search-bar-container {
          position: relative;
          flex: 1;
          max-width: 420px;
        }

        .search-bar-container input {
          width: 100%;
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(12px);
          border: 1px solid var(--glass-border);
          border-radius: 40px;
          padding: 14px 20px 14px 50px;
          color: white;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .search-bar-container input:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 4px rgba(59,130,246,0.1);
          background: rgba(15, 23, 42, 0.7);
        }

        .search-bar-container input::placeholder {
          color: var(--text-dim);
        }

        .search-icon-pos {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-dim);
          opacity: 0.6;
        }

        /* ---- Filtres ---- */
        .filters-bar .form-select {
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(12px);
          border: 1px solid var(--glass-border);
          color: white;
          border-radius: 40px;
          padding: 10px 20px;
          min-width: 150px;
          transition: all 0.3s;
        }

        .filters-bar .form-select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }

        .filters-bar .form-select option {
          background: #0f172a;
          color: white;
        }

        /* ---- Boutons ---- */
        .btn-glow {
          background: var(--glass-bg);
          backdrop-filter: blur(12px);
          border: 1px solid var(--glass-border);
          color: var(--text-main);
          transition: all 0.3s ease;
          border-radius: 40px;
          padding: 10px 24px;
          font-weight: 500;
        }

        .btn-glow:hover {
          background: rgba(255,255,255,0.05);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.3);
        }

        .btn-primary-glow {
          background: var(--accent-blue);
          border: none;
          color: white;
          box-shadow: 0 4px 20px rgba(59,130,246,0.3);
        }

        .btn-primary-glow:hover {
          background: var(--accent-dark-blue, #1d4ed8);
          box-shadow: 0 8px 30px rgba(59,130,246,0.4);
          transform: translateY(-2px);
        }

        /* ---- Modale glass ---- */
        .monetra-modal .modal-content {
          background: rgba(8, 14, 30, 0.85);
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 32px;
          color: white;
          box-shadow: 0 32px 80px rgba(0,0,0,0.7);
        }

        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(255,255,255,0.04);
          padding: 1.5rem 1.8rem;
        }

        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(255,255,255,0.04);
          padding: 1.2rem 1.8rem;
        }

        .form-section-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: var(--accent-cyan);
          letter-spacing: 0.5px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          padding-bottom: 8px;
          margin: 1.8rem 0 1.2rem;
        }

        .form-section-title .icon {
          margin-right: 8px;
          opacity: 0.7;
        }

        .form-control, .form-select {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          color: white;
          border-radius: 14px;
          padding: 12px 16px;
          transition: all 0.3s;
        }

        .form-control:focus, .form-select:focus {
          background: rgba(255,255,255,0.06);
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 4px rgba(59,130,246,0.08);
          color: white;
        }

        .form-control::placeholder {
          color: var(--text-dim);
        }

        .form-select option {
          background: #0f172a;
          color: white;
        }

        .invalid-feedback {
          color: #f87171;
          font-size: 0.8rem;
        }

        .photo-upload-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
        }

        .photo-preview {
          width: 160px;
          height: 160px;
          border-radius: 28px;
          object-fit: cover;
          border: 2px solid rgba(255,255,255,0.06);
          background: #1e293b;
          box-shadow: 0 8px 30px rgba(0,0,0,0.4);
          transition: all 0.3s;
        }

        .photo-preview:hover {
          transform: scale(1.02);
          border-color: var(--accent-blue);
        }

        /* ---- Toast ---- */
        .toast-custom {
          background: rgba(8, 14, 30, 0.85);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 14px 20px;
          color: white;
        }

        /* ---- Scrollbar ---- */
        .modal-body::-webkit-scrollbar {
          width: 6px;
        }
        .modal-body::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.02);
          border-radius: 10px;
        }
        .modal-body::-webkit-scrollbar-thumb {
          background: var(--accent-blue);
          border-radius: 10px;
        }

        /* ---- Responsive ---- */
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }
          .search-bar-container {
            max-width: 100%;
          }
          .filters-bar .form-select {
            min-width: 120px;
            flex: 1;
          }
          .emp-grid-card {
            margin-top: 30px;
            padding: 1.2rem;
          }
          .emp-photo-container {
            width: 72px;
            height: 72px;
            margin-top: -48px;
          }
        }
      `}</style>

      <div className="container-fluid">
        {/* Header Section */}
        <div className="page-header">
          <div>
            <h2 className="fw-bold mb-0">
              <span>👥 Équipe</span> & Collaborateurs
            </h2>
            <p className="text-secondary small" style={{ opacity: 0.7 }}>
              Source: <span className="text-info">{dataSource}</span> • {employees.length} membres
            </p>
          </div>
          <div className="d-flex gap-3">
            <button
              className="btn btn-glow"
              onClick={() => fetchEmployees(1, searchTerm, true)}
              disabled={refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} className="me-2" />
              Actualiser
            </button>
            <button
              className="btn btn-primary-glow"
              onClick={openCreateModal}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Nouveau
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="glass-card p-3 mb-4 d-flex flex-wrap align-items-center gap-3 filters-bar">
          <div className="search-bar-container">
            <FontAwesomeIcon icon={faSearch} className="search-icon-pos" />
            <input
              type="text"
              placeholder="Rechercher un nom, matricule..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <select
            className="form-select w-auto"
            onChange={(e) => setFilterService(e.target.value)}
          >
            <option value="">Tous les services</option>
            {uniqueServices.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="form-select w-auto"
            onChange={(e) => setFilterStatut(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="Actif">Actif</option>
            <option value="Inactif">Inactif</option>
          </select>
          <button
            className="btn btn-glow ms-auto"
            onClick={() => setShowImportModal(true)}
          >
            <FontAwesomeIcon icon={faFileCsv} className="me-2" />
            Import CSV
          </button>
        </div>

        {/* Grid Content */}
        {loading && !refreshing ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted">Synchronisation des données...</p>
          </div>
        ) : (
          <Row className="g-4">
            {filteredList.map((emp, index) => (
              <Col key={emp.id} xs={12} sm={6} md={4} lg={3} style={{ '--i': index }}>
                <div className="glass-card emp-grid-card">
                  <span className={`status-pill ${emp.est_actif ? 'active' : 'inactive'}`}>
                    {emp.est_actif ? 'ACTIF' : 'INACTIF'}
                  </span>

                  <div className="emp-photo-container">
                    {getPhotoUrl(emp.photo) ? (
                      <img src={getPhotoUrl(emp.photo)} alt={emp.nom} />
                    ) : (
                      <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                        <FontAwesomeIcon icon={faUser} size="2x" />
                      </div>
                    )}
                  </div>

                  <h5 className="emp-name">{emp.prenom} {emp.nom}</h5>
                  <span className="emp-role">{emp.fonction || 'Poste non défini'}</span>

                  <div className="emp-info-box">
                    <div>
                      <span className="info-label">Matricule</span>
                      <span className="info-value">#{emp.matricule}</span>
                    </div>
                    <div>
                      <span className="info-label">Service</span>
                      <span className="info-value">{emp.service || '--'}</span>
                    </div>
                    <div>
                      <span className="info-label">Téléphone</span>
                      <span className="info-value">{emp.telephone || '--'}</span>
                    </div>
                    <div>
                      <span className="info-label">Salaire</span>
                      <span className="info-value text-success">{emp.salaire_base} USD</span>
                    </div>
                  </div>

                  <div className="card-actions-row">
                    <Link to={`/profil-employe/${emp.id}`} className="action-btn" title="Voir Profil">
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                    <button className="action-btn" onClick={() => openEditModal(emp)} title="Modifier">
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => { setFormData(emp); setShowDeleteModal(true); }}
                      title="Supprimer"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-5 gap-3">
          <Button
            variant="outline-light"
            className="rounded-pill px-4 btn-glow"
            disabled={pagination.currentPage === 1}
            onClick={() => fetchEmployees(pagination.currentPage - 1, searchTerm)}
          >
            Précédent
          </Button>
          <div className="d-flex align-items-center px-4 glass-card" style={{ borderRadius: '40px' }}>
            {pagination.currentPage} / {pagination.totalPages}
          </div>
          <Button
            variant="outline-light"
            className="rounded-pill px-4 btn-glow"
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => fetchEmployees(pagination.currentPage + 1, searchTerm)}
          >
            Suivant
          </Button>
        </div>
      </div>

      {/* ================================================================
          MODALE FORMULAIRE COMPLET
          ================================================================ */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton className="border-0">
            <Modal.Title>
              {editingEmployee ? (
                <><FontAwesomeIcon icon={faEdit} className="me-2" /> Modifier le collaborateur</>
              ) : (
                <><FontAwesomeIcon icon={faPlus} className="me-2" /> Nouveau collaborateur</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Photo */}
            <Row className="mb-4">
              <Col md={3} className="text-center">
                <div className="photo-upload-wrapper">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Aperçu" className="photo-preview" />
                  ) : (
                    <div className="photo-preview d-flex align-items-center justify-content-center text-muted">
                      <FontAwesomeIcon icon={faImage} size="3x" />
                    </div>
                  )}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    className="bg-dark text-white border-0"
                    style={{ padding: '8px', borderRadius: '40px' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPhotoFile(file);
                        setPhotoPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                </div>
              </Col>
              <Col md={9}>
                <div className="form-section-title">
                  <FontAwesomeIcon icon={faUser} className="icon" /> IDENTITÉ & ÉTAT CIVIL
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Matricule</Form.Label>
                    <Form.Control
                      name="matricule"
                      value={formData.matricule || ''}
                      onChange={handleInputChange}
                      placeholder="Auto-généré si vide"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      name="nom"
                      value={formData.nom || ''}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.nom}
                    />
                    <Form.Control.Feedback type="invalid">{formErrors.nom}</Form.Control.Feedback>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      name="prenom"
                      value={formData.prenom || ''}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.prenom}
                    />
                    <Form.Control.Feedback type="invalid">{formErrors.prenom}</Form.Control.Feedback>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Postnom</Form.Label>
                    <Form.Control name="postnom" value={formData.postnom || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Sexe</Form.Label>
                    <Form.Select name="sexe" value={formData.sexe || ''} onChange={handleInputChange}>
                      <option value="">Non spécifié</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Situation familiale</Form.Label>
                    <Form.Select name="etat_civil" value={formData.etat_civil || 'celibataire'} onChange={handleInputChange}>
                      <option value="celibataire">Célibataire</option>
                      <option value="marie">Marié(e)</option>
                      <option value="divorce">Divorcé(e)</option>
                      <option value="veuf">Veuf/Veuve</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Nombre d'enfants</Form.Label>
                    <Form.Control type="number" name="nombre_enfants" value={formData.nombre_enfants || 0} onChange={handleInputChange} min="0" />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Date de naissance</Form.Label>
                    <Form.Control type="date" name="date_naissance" value={formData.date_naissance || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Lieu de naissance</Form.Label>
                    <Form.Control name="lieu_naissance" value={formData.lieu_naissance || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Nationalité</Form.Label>
                    <Form.Control name="nationalite" value={formData.nationalite || 'Congolaise'} onChange={handleInputChange} />
                  </Col>
                </Row>

                <div className="form-section-title">
                  <FontAwesomeIcon icon={faAddressCard} className="icon" /> COORDONNÉES & ADRESSE
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" name="email" value={formData.email || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Téléphone</Form.Label>
                    <Form.Control name="telephone" value={formData.telephone || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Téléphone 2</Form.Label>
                    <Form.Control name="telephone2" value={formData.telephone2 || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Province</Form.Label>
                    <Form.Control name="province" value={formData.province || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Ville</Form.Label>
                    <Form.Control name="ville" value={formData.ville || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Commune</Form.Label>
                    <Form.Control name="commune" value={formData.commune || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Quartier</Form.Label>
                    <Form.Control name="quartier" value={formData.quartier || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Adresse</Form.Label>
                    <Form.Control name="adresse" value={formData.adresse || ''} onChange={handleInputChange} />
                  </Col>
                </Row>

                <div className="form-section-title">
                  <FontAwesomeIcon icon={faPhone} className="icon" /> CONTACT D'URGENCE
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Personne à contacter</Form.Label>
                    <Form.Control name="personne_urgence" value={formData.personne_urgence || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Téléphone urgence</Form.Label>
                    <Form.Control name="telephone_urgence" value={formData.telephone_urgence || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Lien de parenté</Form.Label>
                    <Form.Control name="lien_parente" value={formData.lien_parente || ''} onChange={handleInputChange} />
                  </Col>
                </Row>

                <div className="form-section-title">
                  <FontAwesomeIcon icon={faBriefcase} className="icon" /> INFORMATIONS PROFESSIONNELLES
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Date d'embauche <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="date_embauche"
                      value={formData.date_embauche || ''}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.date_embauche}
                    />
                    <Form.Control.Feedback type="invalid">{formErrors.date_embauche}</Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Date de départ</Form.Label>
                    <Form.Control type="date" name="date_depart" value={formData.date_depart || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Service</Form.Label>
                    <Form.Control name="service" value={formData.service || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                   <Form.Label>Département</Form.Label>
<Form.Select
  name="departement"
  value={formData.departement || ''}
  onChange={handleInputChange}
>
  <option value="">Sélectionner un département</option>
  <option value="Interne">Interne</option>
  <option value="Externe">Externe</option>
</Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Fonction</Form.Label>
                    <Form.Control name="fonction" value={formData.fonction || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Catégorie professionnelle</Form.Label>
                    <Form.Control name="categorie_professionnelle" value={formData.categorie_professionnelle || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Niveau d'étude</Form.Label>
                    <Form.Control name="niveau_etude" value={formData.niveau_etude || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Type de contrat</Form.Label>
                    <Form.Select name="type_contrat" value={formData.type_contrat || 'CDI'} onChange={handleInputChange}>
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="Stage">Stage</option>
                      <option value="Apprentissage">Apprentissage</option>
                      <option value="Consultant">Consultant</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Début contrat</Form.Label>
                    <Form.Control type="date" name="date_debut_contrat" value={formData.date_debut_contrat || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Fin contrat</Form.Label>
                    <Form.Control type="date" name="date_fin_contrat" value={formData.date_fin_contrat || ''} onChange={handleInputChange} />
                  </Col>
                </Row>

                <div className="form-section-title">
                  <FontAwesomeIcon icon={faDollarSign} className="icon" /> RÉMUNÉRATION & BANQUE
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Salaire base <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="salaire_base"
                      value={formData.salaire_base || 0}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.salaire_base}
                    />
                    <Form.Control.Feedback type="invalid">{formErrors.salaire_base}</Form.Control.Feedback>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Devise</Form.Label>
                    <Form.Select name="devise" value={formData.devise || 'USD'} onChange={handleInputChange}>
                      <option value="USD">USD</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Banque</Form.Label>
                    <Form.Control name="banque" value={formData.banque || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Compte bancaire</Form.Label>
                    <Form.Control name="compte_bancaire" value={formData.compte_bancaire || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>État</Form.Label>
                    <Form.Select name="est_actif" value={formData.est_actif} onChange={handleInputChange}>
                      <option value={1}>Actif</option>
                      <option value={0}>Inactif</option>
                    </Form.Select>
                  </Col>
                </Row>

                <div className="form-section-title">
                  <FontAwesomeIcon icon={faIdCard} className="icon" /> DOCUMENTS & NUMÉROS OFFICIELS
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>N° CNSS</Form.Label>
                    <Form.Control name="numero_cnss" value={formData.numero_cnss || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>N° INPP</Form.Label>
                    <Form.Control name="numero_inpp" value={formData.numero_inpp || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>N° NIF</Form.Label>
                    <Form.Control name="numero_nif" value={formData.numero_nif || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Pièce d'identité</Form.Label>
                    <Form.Control name="piece_identite" value={formData.piece_identite || ''} onChange={handleInputChange} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Numéro pièce</Form.Label>
                    <Form.Control name="numero_piece" value={formData.numero_piece || ''} onChange={handleInputChange} />
                  </Col>
                </Row>

                <div className="form-section-title">
                  <FontAwesomeIcon icon={faStethoscope} className="icon" /> OBSERVATIONS
                </div>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Control as="textarea" rows="3" name="observation" value={formData.observation || ''} onChange={handleInputChange} placeholder="Notes, remarques..." />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4">
            <Button variant="outline-light" className="rounded-pill px-4 btn-glow" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" className="rounded-pill px-5 btn-primary-glow">
              {editingEmployee ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ================================================================
          MODALE SUPPRESSION
          ================================================================ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger">⚠️ Confirmation de suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Voulez-vous vraiment supprimer <strong>{formData.prenom} {formData.nom}</strong> ? Cette action est irréversible.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-light" className="rounded-pill px-4 btn-glow" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" className="rounded-pill px-4" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>  
      </Modal>

      {/* ================================================================
          TOAST NOTIFICATION
          ================================================================ */}
      {toast.show && (
        <div className={`toast align-items-center text-white bg-${toast.variant} border-0 show position-fixed top-0 end-0 m-3 toast-custom`} style={{ zIndex: 1100 }}>
          <div className="d-flex">
            <div className="toast-body">
              <FontAwesomeIcon icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle} className="me-2" />
              {toast.message}
            </div>
            <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast({ ...toast, show: false })} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEmployes;